import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { requireSessionUser } from '@/integrations/supabase/session';
import { toast } from '@/hooks/use-toast';
import { useCompanyProfile } from './useCompanyProfile';
import { useTenant } from './useTenant';

export type InviteRole = 'admin' | 'csr' | 'affiliate' | 'tech';

export interface Invite {
  id: string;
  company_id: string;
  email: string;
  role: InviteRole;
  /** Set only for role 'tech': the techs row the login links to (DB CHECK enforces the pairing). */
  tech_id: string | null;
  token_hash: string;
  expires_at: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  invited_by_user_id: string;
  created_at: string;
  updated_at: string;
}

/**
 * Send the invite email through send-invite-email. Returns null when it went
 * out, else the reason. functions.invoke resolves (never throws) on a non-2xx,
 * so the error has to be read off the result.
 */
async function sendInviteEmail(body: { email: string; inviteToken: string; companyName: string; invitedByName: string }): Promise<string | null> {
  try {
    const { error } = await supabase.functions.invoke('send-invite-email', { body });
    if (!error) return null;
    const ctx = (error as { context?: unknown }).context as Response | undefined;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const parsed = await ctx.clone().json();
        if (parsed?.error) return String(parsed.error);
      } catch {
        /* fall through */
      }
    }
    return error.message || 'The invite email could not be sent';
  } catch (err) {
    return err instanceof Error ? err.message : 'The invite email could not be sent';
  }
}

export function useInvites() {
  const { data: companyProfile } = useCompanyProfile();
  
  return useQuery({
    queryKey: ['invites', companyProfile?.id],
    queryFn: async () => {
      if (!companyProfile?.id) return [];

      const { data, error } = await supabase
        .from('invites')
        .select('*')
        .eq('company_id', companyProfile.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get user names for invited_by
      const userIds = [...new Set(data.map(inv => inv.invited_by_user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);

      const profileMap = new Map(profiles?.map(p => [p.id, p.full_name]) || []);

      return data.map(invite => ({
        ...invite,
        invited_by: { full_name: profileMap.get(invite.invited_by_user_id) || 'Unknown' }
      }));
    },
    enabled: !!companyProfile?.id,
  });
}

export function useCreateInvite() {
  const queryClient = useQueryClient();
  const { data: companyProfile } = useCompanyProfile();
  const { me } = useTenant();

  return useMutation({
    mutationFn: async ({ email, role, techId = null }: { email: string; role: InviteRole; techId?: string | null }) => {
      if ((role === 'tech') !== !!techId) throw new Error('A technician invite must name the technician it is for');
      if (!companyProfile?.id) throw new Error('No company profile found');

      const user = await requireSessionUser();

      // CRITICAL: Validate company_id is not NULL UUID
      if (companyProfile.id === '00000000-0000-0000-0000-000000000000') {
        console.error('❌ CRITICAL: Invalid company_id detected!', {
          user_id: user.id,
          company_id: companyProfile.id,
          user_email: user.email
        });
        throw new Error(
          'Your company profile is incomplete. Please complete your company setup first, ' +
          'or log out and log back in to refresh your session.'
        );
      }

      console.log('📧 Creating invite with company_id:', companyProfile.id, 'User ID:', user.id);

      // Generate secure random token (32 bytes = 64 hex chars)
      const tokenArray = new Uint8Array(32);
      crypto.getRandomValues(tokenArray);
      const token = Array.from(tokenArray, byte => byte.toString(16).padStart(2, '0')).join('');
      
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 72); // 72 hours

      // Hash the token before storing
      const { data: hashData, error: hashError } = await supabase.rpc('hash_token', { token });
      if (hashError) {
        console.error('❌ Failed to hash token:', hashError);
        throw hashError;
      }

      console.log('🔐 Token hashed successfully, inserting invite...');

      const { data, error } = await supabase
        .from('invites')
        .insert([{
          company_id: companyProfile.id,
          email,
          role,
          tech_id: techId,
          token: '', // Deprecated field, kept for backward compatibility
          token_hash: hashData,
          expires_at: expiresAt.toISOString(),
          invited_by_user_id: user.id,
        }])
        .select()
        .single();

      if (error) {
        console.error('❌ Error creating invite:', error);
        throw error;
      }

      // CRITICAL: Verify the inserted invite has correct company_id
      if (data.company_id === '00000000-0000-0000-0000-000000000000') {
        console.error('⚠️ WARNING: Invite was created with NULL UUID company_id!', {
          invite_id: data.id,
          company_id: data.company_id
        });
        throw new Error('Invite was created incorrectly. Please contact support.');
      }

      console.log('✅ Invite created successfully:', {
        id: data.id,
        email: data.email,
        company_id: data.company_id,
        role: data.role,
        status: data.status
      });

      // Send invite email. The invite row stays either way, so a failed send can be retried with Resend.
      const emailError = await sendInviteEmail({
        email,
        inviteToken: token,
        companyName: companyProfile.name || 'the team',
        invitedByName: me?.fullName || 'Team member',
      });
      if (emailError) console.error('❌ Failed to send invite email:', emailError);

      return { ...data, emailError };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      queryClient.invalidateQueries({ queryKey: ['techs'] });
      queryClient.invalidateQueries({ queryKey: ['techs-settings'] });
      if (data.emailError) {
        toast({
          title: 'Invite saved, but the email was not sent',
          description: `${data.emailError.replace(/\.$/, '')}. Use Resend in the invites list to try again.`,
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: 'Invite sent',
        description: `The invitation email was sent to ${data.email}.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to send invite',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useRevokeInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { error } = await supabase
        .from('invites')
        .update({ status: 'revoked' })
        .eq('id', inviteId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      toast({
        title: 'Invite revoked',
        description: 'The invitation has been revoked successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to revoke invite',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { error } = await supabase
        .from('invites')
        .delete()
        .eq('id', inviteId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      toast({
        title: 'Invite deleted',
        description: 'The invitation has been permanently removed.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete invite',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useResendInvite() {
  const queryClient = useQueryClient();
  const { data: companyProfile } = useCompanyProfile();
  const { me } = useTenant();

  return useMutation({
    mutationFn: async (inviteId: string) => {
      await requireSessionUser();

      // Generate secure random token
      const tokenArray = new Uint8Array(32);
      crypto.getRandomValues(tokenArray);
      const newToken = Array.from(tokenArray, byte => byte.toString(16).padStart(2, '0')).join('');

      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 72);

      // Hash the new token
      const { data: hashData, error: hashError } = await supabase.rpc('hash_token', { token: newToken });
      if (hashError) throw hashError;

      // Update with new hashed token
      const { data, error } = await supabase
        .from('invites')
        .update({
          token: '', // Deprecated field
          token_hash: hashData,
          expires_at: expiresAt.toISOString(),
          status: 'pending',
        })
        .eq('id', inviteId)
        .select()
        .single();

      if (error) throw error;

      // Send invite email. The token was just rotated, so a failed send leaves no working link: surface it.
      const emailError = await sendInviteEmail({
        email: data.email,
        inviteToken: newToken,
        companyName: companyProfile?.name || 'the team',
        invitedByName: me?.fullName || 'Team member',
      });
      if (emailError) {
        queryClient.invalidateQueries({ queryKey: ['invites'] });
        throw new Error(`The email to ${data.email} was not sent: ${emailError}`);
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      toast({
        title: 'Invite resent',
        description: `A new invitation email was sent to ${data.email}.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to resend invite',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
